import React, { useContext, useEffect } from 'react';

import { SPAViewContext } from '@confluence/spa-view-context';
import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { markErrorAsHandled } from '@confluence/graphql';
import { isUnauthorizedError } from '@confluence/error-boundary';

import { useUserInfo } from '../store/userInfoStore';
import { useLoadUserCanPayAndUpgradeToStore } from '../hooks/load-to-store/useLoadUserCanPayAndUpgradeToStore';
import { useLoadTrialInfoToStore } from '../hooks/load-to-store/useLoadTrialInfoToStore';
import { useLoadBillingInformationToStore } from '../hooks/load-to-store/useLoadBillingInformationToStore';
import { useExperimentInfoActions } from '../store/experimentInfoStore';

import { DataLoader } from './DataLoader';

export const EotTrialInfo = ({ children }: { children: JSX.Element }) => {
	const { userCanPayAndUpgrade, modalKey, daysLeftInTrial } = useUserInfo();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const { setShouldShowModal } = useExperimentInfoActions();
	const { edition } = useSessionData();
	const isStandardEdition = edition === ConfluenceEdition.STANDARD;
	const { timeLeftTrialLoading, timeLeftTrialError } = useLoadTrialInfoToStore(
		!isStandardEdition || !isSiteAdmin,
	);

	const isInLastWeekOfTrial = !!(daysLeftInTrial && daysLeftInTrial > 0 && daysLeftInTrial <= 7);

	const skip = !!(
		!isStandardEdition ||
		!isInLastWeekOfTrial ||
		!isSiteAdmin ||
		timeLeftTrialLoading ||
		timeLeftTrialError
	);

	useLoadUserCanPayAndUpgradeToStore(skip);
	useLoadBillingInformationToStore(skip);

	// if experiment is on and they are eligible, show the experiment
	// they are eligible if they are:
	// - in the standard edition
	// - are a site admin
	// - in trial
	// - are within a week of the end of their trial

	const shouldShowModal =
		edition === ConfluenceEdition.STANDARD &&
		isInLastWeekOfTrial &&
		isSiteAdmin &&
		!!modalKey &&
		!!userCanPayAndUpgrade;

	useEffect(() => {
		void setShouldShowModal(shouldShowModal);
	}, [setShouldShowModal, shouldShowModal]);

	// `TrialDetailsQuery` can throw Permission exception if initiater doesn't have sufficient permission
	// Permission error in this case is an expected error and FE should handle it gracefully via `markErrorAsHandled`
	// https://pug.jira-dev.com/wiki/spaces/CFE/blog/2021/03/02/17874389778/Please+handle+GraphQL+errors+properly
	if (timeLeftTrialError && isUnauthorizedError(timeLeftTrialError)) {
		markErrorAsHandled(timeLeftTrialError);
	}

	// it's just easier to split the logic this way for the sake of the hook in TrialFlags.tsx
	if (shouldShowModal) {
		return <DataLoader>{children}</DataLoader>;
	}

	return <>{children}</>;
};
